import type { CreateTruckInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { useTruckDevice } from '../../../../integrations/features/gps/public/use-truck-device';
import { truckSavedNotice } from '../policies/truck-form-device.policy';
import { createTruck } from '../trucks.api';

/** Creating a truck from the Units list; a form failure stays inline (CLAUDE.md → Web conventions). */
export function useCreateTruck(onCreated: () => void) {
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const toast = useToast();
  const { apply: applyNewTruckDevice } = useTruckDevice();

  /** Returns whether it succeeded, so the drawer knows to close itself. */
  async function create(input: CreateTruckInput, device: string | undefined): Promise<boolean> {
    setCreateError(null);
    setCreating(true);
    try {
      const truck = await createTruck(input);
      // The truck exists from here on, so a failed device call closes the drawer too: a retry would only 409 on the registration.
      const notice = truckSavedNotice(`${truck.registration} created`, await applyNewTruckDevice(truck.id, device));
      onCreated();
      toast[notice.kind](notice.message);
      return true;
    } catch (err) {
      setCreateError(
        err instanceof ApiError && err.status === 409
          ? 'A truck with this registration already exists.'
          : 'Could not create the truck — check the fields and try again.',
      );
      return false;
    } finally {
      setCreating(false);
    }
  }

  return { createError, creating, create, resetCreateError: () => setCreateError(null) };
}
